/** @format */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import AddIcon from '@mui/icons-material/Add';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import TodayIcon from '@mui/icons-material/Today';
import {
  Alert,
  Box,
  Button,
  capitalize,
  InputAdornment,
  LinearProgress,
  Tab,
  Tooltip,
  Typography,
} from '@mui/material';
import {clsx} from 'clsx';
import {
  add,
  addDays,
  endOfDay,
  endOfMonth,
  format,
  isBefore,
  isWithinInterval,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import {ru} from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import {Trans, useTranslation} from 'react-i18next';

import {useNavigate, useSearchParams} from 'react-router-dom';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import LegacyTextField from 'components/inputs/LegacyTextField';

import SidebarNew from 'components/MediaplanSidebar/SidebarNew';

import SidebarEdit from 'components/MediaplanSidebar/SidebarEdit';

import BackToProjectButton from 'components/BackToProjectButton';

import {
  AccountsMediaplanCreateInput,
  AccountsMediaplanRepeat,
  AccountsMediaplanType,
  AccountsMediaplanUpdateInput,
} from 'graphql/generated/types';

import Chart from './components/Chart';
import KpiPlanForm from './components/KpiPlanForm';
import {classes, CONTROLS_PERIODS, FAR_FUTURE} from './constants';
import {
  useGetAnalyticGoalsQuery,
  useGetCompanyMediaplansQuery,
  useGetCurrentUserQuery,
  useSaveKpiSettingsMutation,
  useUpdateMediaplansMutation,
} from './graphql/__generated__';
import {
  ControlsPeriod,
  KpiLocalSettings,
  Period,
  PeriodType,
  YalperAnalyticAttributionModelEnum,
} from './interfaces';
import {StyledBox, StyledTabs, Switch} from './styles';
import {
  calculateDailyBudget,
  formatBudgetPlan,
  getBusyIntervalsCb,
  getDayOfWeekInGenitive,
  getRepeatOn,
  getType,
  isRangeOverlapping,
  parseFormattedNumber,
  transformKpis,
  transformKpiToLocalSettings,
} from './utils';

const Periods = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const hasMultipleSystems = searchParams.get('hasMultipleSystems') === 'true';
  const isEdit = searchParams.get('edit');

  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();

  const [activeControlsPeriod, setActiveControlsPeriod] = useState<ControlsPeriod>('quarter');

  const [periods, setPeriods] = useState<Period[]>([]);
  const [activePeriodId, setActivePeriodId] = useState<string | null>(null);

  const startDatePickerRef = useRef<DatePicker | null>(null);
  const endDatePickerRef = useRef<DatePicker | null>(null);

  const {loading, error, data, refetch} = useGetCurrentUserQuery({
    variables: {companyId},
  });
  const {
    data: dataGoals,
    loading: loadingGoals,
    error: errorGoals,
  } = useGetAnalyticGoalsQuery({
    variables: {companyId},
  });
  const {
    data: dataCompanyMediaplans,
    loading: loadingCompanyMediaplans,
    error: errorCompanyMediaplans,
  } = useGetCompanyMediaplansQuery({
    variables: {
      companyId,
      startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      endDate: format(add(new Date(), {years: 1}), 'yyyy-MM-dd'),
    },
  });

  const [updateMediaplans] = useUpdateMediaplansMutation();
  const [saveKpiSettings] = useSaveKpiSettingsMutation();

  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);

  const originalUnfinished = useMemo(
    () => data?.currentUser?.company?.unfinishedMediaplans || [],
    [data],
  );

  const originalIds = useMemo(() => originalUnfinished.map(p => p.id), [originalUnfinished]);

  const companyMediaplans = useMemo(
    () => dataCompanyMediaplans?.currentUser?.company?.mediaplans || [],
    [dataCompanyMediaplans],
  );

  const externalMediaplans = useMemo(
    () => companyMediaplans.filter(m => !originalIds.includes(m.id)),
    [companyMediaplans, originalIds],
  );

  const lastFinishedEndDate = useMemo(() => {
    const today = new Date();
    const finished = companyMediaplans
      .map(m => ({
        id: m.id,
        start: m.startOn ? new Date(m.startOn) : null,
        end: m.endOn ? new Date(m.endOn) : null,
      }))
      .filter((m: {end: Date | null}) => m.end !== null && (m.end as Date) < today);

    if (finished.length === 0) return null;

    return finished.reduce((acc: Date | null, cur) => {
      if (!acc) return cur.end as Date;
      return (cur.end as Date) > acc ? (cur.end as Date) : acc;
    }, null);
  }, [companyMediaplans]);

  const minStartDate = useMemo(() => {
    const firstDayOfMonth = startOfDay(startOfMonth(new Date()));
    if (!lastFinishedEndDate) return firstDayOfMonth;
    const nextDayAfterFinished = startOfDay(addDays(startOfDay(lastFinishedEndDate), 1));
    return new Date(Math.max(firstDayOfMonth.getTime(), nextDayAfterFinished.getTime()));
  }, [lastFinishedEndDate]);

  const farFuture = useMemo(() => startOfDay(FAR_FUTURE), []);

  const isDateInRange = useCallback(
    (date: Date, start?: Date | null, end?: Date | null) => {
      if (!start) return false;
      const normalizedDate = startOfDay(date);
      const normalizedStart = startOfDay(start);
      const normalizedEnd = end ? endOfDay(startOfDay(end)) : farFuture;
      return isWithinInterval(normalizedDate, {
        start: normalizedStart,
        end: normalizedEnd,
      });
    },
    [farFuture],
  );

  const isDayBusy = useCallback(
    (date: Date, excludePeriodId?: string | null) => {
      const busyExternal = externalMediaplans.some(m =>
        isDateInRange(
          date,
          m.startOn ? new Date(m.startOn) : null,
          m.endOn ? new Date(m.endOn) : null,
        ),
      );
      if (busyExternal) return true;

      return periods.some((p: Period) => {
        if (excludePeriodId && p.id === excludePeriodId) return false;
        return isDateInRange(date, p.startDate, p.endDate);
      });
    },
    [externalMediaplans, periods, isDateInRange],
  );

  const getBusyIntervals = useCallback(getBusyIntervalsCb, [externalMediaplans, periods]);

  const canSelectStartDate = useCallback(
    (date: Date, currentId?: string | null) => {
      if (minStartDate && isBefore(date, minStartDate)) return false;
      return !isDayBusy(date, currentId || null);
    },
    [minStartDate, isDayBusy],
  );

  const canSelectEndDate = useCallback(
    (date: Date, current: Period | undefined | null) => {
      if (!current?.startDate) return false;
      if (isBefore(date, current.startDate)) return false;
      return !isRangeOverlapping(
        current.startDate,
        date,
        getBusyIntervals(externalMediaplans, periods, current.id),
        current.id,
        farFuture,
      );
    },
    [getBusyIntervals, externalMediaplans, periods, farFuture],
  );

  const isDateBusyByFinished = useCallback(
    (date: Date) => {
      const today = new Date();
      return externalMediaplans.some(m => {
        const start = m.startOn ? new Date(m.startOn) : null;
        const end = m.endOn ? new Date(m.endOn) : null;
        if (!start || !end) return false;
        if (end >= today) return false;
        return isDateInRange(date, start, end);
      });
    },
    [externalMediaplans, isDateInRange],
  );

  const renderDayWithHint = useCallback(
    (day: number, date?: Date) => {
      if (!date) return day;
      const busyFinished = isDateBusyByFinished(date);
      if (busyFinished) {
        return (
          <Tooltip
            title={t('Дата занята завершённым медиапланом', 'Дата занята завершённым медиапланом')}
          >
            <Box component='span'>{day}</Box>
          </Tooltip>
        );
      }
      const busyByMediaplan = isDayBusy(date, activePeriodId);
      if (busyByMediaplan) {
        return (
          <Tooltip title={t('Дата занята другим медиапланом', 'Дата занята другим медиапланом')}>
            <Box component='span'>{day}</Box>
          </Tooltip>
        );
      }
      return day;
    },
    [isDateBusyByFinished, isDayBusy, activePeriodId, t],
  );

  const getNextBusyStartAfter = useCallback(
    (date: Date, excludeId?: string | null): Date | null => {
      const candidateDates: Date[] = [];
      externalMediaplans.forEach(m => {
        if (m.startOn) {
          const s = new Date(m.startOn);
          if (isBefore(date, s)) candidateDates.push(s);
        }
      });
      periods.forEach((p: Period) => {
        if (excludeId && p.id === excludeId) return;
        if (p.startDate && isBefore(date, p.startDate)) {
          candidateDates.push(p.startDate);
        }
      });
      if (candidateDates.length === 0) return null;
      return candidateDates.reduce((min: Date, d: Date) => (isBefore(d, min) ? d : min));
    },
    [externalMediaplans, periods],
  );

  const computeRepeatableEndDate = useCallback(
    (start: Date, excludeId?: string | null): Date => {
      const nextBusyStart = getNextBusyStartAfter(start, excludeId ?? null);
      const oneYearEnd = endOfMonth(add(start, {years: 1}));
      if (nextBusyStart && isBefore(nextBusyStart, oneYearEnd)) {
        const endOfMonthBeforeBusyStart = endOfMonth(add(startOfDay(nextBusyStart), {months: -1}));
        if (!isBefore(endOfMonthBeforeBusyStart, start)) {
          return endOfMonthBeforeBusyStart;
        }
        return add(nextBusyStart, {days: -1});
      }
      return oneYearEnd;
    },
    [getNextBusyStartAfter],
  );

  const handlePeriodChange = (key: ControlsPeriod) => {
    setActiveControlsPeriod(key);
  };

  const handlePeriodSwipe = useCallback(
    (value: number) => {
      if (!startDate || !endDate) return;

      const newStartDate = new Date(startDate);
      const newEndDate = new Date(endDate);

      let monthsToAdd;
      switch (activeControlsPeriod) {
        case 'quarter':
          monthsToAdd = 3;
          break;
        case 'half-year':
          monthsToAdd = 6;
          break;
        case 'year':
          monthsToAdd = 12;
          break;
        default:
          monthsToAdd = 3;
      }

      newStartDate.setMonth(newStartDate.getMonth() + monthsToAdd * value);
      newEndDate.setMonth(newEndDate.getMonth() + monthsToAdd * value);

      setStartDate(newStartDate);
      setEndDate(newEndDate);
    },
    [activeControlsPeriod, startDate, endDate],
  );

  const handleAddPeriod = () => {
    const addedPeriodId = String(Date.now());

    const periodsWithEndDate = periods
      .filter(p => p.endDate)
      .sort((a, b) => b.endDate!.getTime() - a.endDate!.getTime());

    const newStartDate =
      periodsWithEndDate.length > 0 ? add(periodsWithEndDate[0].endDate!, {days: 1}) : null;

    const computedEndDate = newStartDate ? computeRepeatableEndDate(newStartDate, null) : null;

    setPeriods([
      ...periods,
      {
        id: addedPeriodId,
        startDate: newStartDate,
        endDate: computedEndDate,
        type: 'repeatable',
        repeatEvery: 'monthly',
        isLinkedWithPeriodStart: true,
        budgetPlan: null,
        kpis: [],
      },
    ]);

    setActivePeriodId(addedPeriodId);
  };

  const handleBudgetPlanChange = (updatedValue: number) => {
    setPeriods(
      periods.map((el: Period) => {
        if (el.id === activePeriodId) {
          return {...el, budgetPlan: updatedValue};
        }

        return el;
      }),
    );
  };
  const handleActiveTabClick = (id: string) => {
    const period = periods.find((el: Period) => el.id === id);

    if (period) {
      setActivePeriodId(period.id);
    }
  };

  const handleTabRemove = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();

    const newPeriods = periods.filter((period: Period) => period.id !== id);

    if (activePeriodId === id) {
      if (newPeriods.length > 0) {
        setActivePeriodId(newPeriods[newPeriods.length - 1].id);
      } else {
        setActivePeriodId(null);
      }
    }

    setPeriods(newPeriods);
  };

  const handleChangePeriodType = (type: PeriodType) => {
    if (type === 'repeatable') {
      const current = periods.find(p => p.id === activePeriodId) || null;
      if (current?.startDate) {
        const endDate = computeRepeatableEndDate(current.startDate, current.id);
        updateRequest({type, endDate});
        return;
      }
    }
    updateRequest({type, endDate: null});
  };

  const updateRequest = useCallback(
    (payload: Partial<Period>) => {
      if (activePeriodId) {
        setPeriods(
          periods.map((el: Period) => {
            if (el.id === activePeriodId) {
              return {...el, ...payload};
            }

            return el;
          }),
        );
      }
    },
    [activePeriodId, periods],
  );

  const handleStartDateChange = (date: Date | null) => {
    if (!date) {
      updateRequest({
        startDate: null,
        endDate: null,
      });
      return;
    }

    if (date < minStartDate || isDayBusy(date, activePeriodId)) {
      return;
    }

    setPeriods(
      periods.map((el: Period) => {
        if (el.id !== activePeriodId) return el;
        if (el.type === 'flight') {
          return {
            ...el,
            startDate: date,
          };
        }
        const endDate = computeRepeatableEndDate(date, el.id);
        return {
          ...el,
          startDate: date,
          endDate,
        };
      }),
    );
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!date) {
      updateRequest({endDate: null});
      return;
    }

    if (!activePeriod?.startDate) return;

    if (
      isRangeOverlapping(
        activePeriod.startDate,
        date,
        getBusyIntervals(externalMediaplans, periods, activePeriodId),
        activePeriodId,
        farFuture,
      )
    ) {
      return;
    }

    updateRequest({endDate: date});
  };

  const handleLinkedWithPeriodStartChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateRequest({isLinkedWithPeriodStart: event.target.checked});
  };

  const mapPeriods = (period: Period) => {
    const isShouldShowEndDate = period.type === 'flight';

    return (
      <Tab
        key={period.id}
        value={period.id}
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              position: 'relative',
            }}
          >
            <Box>
              {period.startDate ? (
                format(new Date(period.startDate), 'd MMMM', {
                  locale: ru,
                })
              ) : (
                <Trans>Новый период</Trans>
              )}
              {period.endDate && isShouldShowEndDate
                ? ` - ${format(new Date(period.endDate), 'd MMMM', {
                    locale: ru,
                  })}`
                : ''}
            </Box>
            <CloseIcon
              sx={{
                fontSize: 20,
                color: '#8B8B8B',
                opacity: 0.7,
                transition: 'opacity 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 1,
                },
              }}
              onClick={e => handleTabRemove(e, period.id)}
            />
          </Box>
        }
      />
    );
  };

  useEffect(() => {
    const unfinishedMediaplans = data?.currentUser?.company?.unfinishedMediaplans;
    if (!unfinishedMediaplans?.length) {
      return;
    }
    const normalizedMediaplans = unfinishedMediaplans.map(el => ({
      id: el.id,
      startDate: new Date(el.startOn),
      endDate: el.endOn ? new Date(el.endOn) : endOfMonth(add(new Date(el.startOn), {years: 1})),
      type: getType(el.type),
      budgetPlan: el.budget,
      repeatEvery: getRepeatOn(el.repeat ?? 'MONTHLY'),
      isLinkedWithPeriodStart: el.startOnPeriodBegin ?? false,
      kpis: el.kpis?.map(kpi => transformKpiToLocalSettings(kpi)) || [],
    }));

    const sortedMediaplans = normalizedMediaplans
      .slice()
      .sort((a, b) => a.startDate!.getTime() - b.startDate!.getTime());

    const adjustedMediaplans = sortedMediaplans.map((period, index) => {
      if (period.type === 'flight') {
        return period;
      }

      const nextPeriod = sortedMediaplans
        .slice(index + 1)
        .find(p => p.startDate && p.startDate > period.startDate!);

      if (nextPeriod && nextPeriod.startDate! < period.endDate!) {
        return {
          ...period,
          endDate: add(nextPeriod.startDate!, {days: -1}),
        };
      }

      return period;
    });

    const firstPeriodId = adjustedMediaplans[0]?.id || null;

    setPeriods(adjustedMediaplans);

    if (firstPeriodId) {
      setActivePeriodId(firstPeriodId);
    }
  }, [data]);

  useEffect(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    let lastDay;

    switch (activeControlsPeriod) {
      case 'quarter':
        lastDay = new Date(now.getFullYear(), now.getMonth() + 3, 0);
        break;
      case 'half-year':
        lastDay = new Date(now.getFullYear(), now.getMonth() + 6, 0);
        break;
      case 'year':
        lastDay = new Date(now.getFullYear(), now.getMonth() + 12, 0);
        break;
      default:
        lastDay = new Date(now.getFullYear(), now.getMonth() + 3, 0);
    }

    setStartDate(firstDayOfMonth);
    setEndDate(lastDay);
  }, [activeControlsPeriod]);

  const activePeriod = useMemo(() => {
    return periods.find((el: Period) => el.id === activePeriodId);
  }, [periods, activePeriodId]);

  const areKpisValid = useMemo(() => {
    if (!activePeriod || !activePeriod.kpis || activePeriod.kpis.length === 0) {
      return true;
    }

    return activePeriod.kpis.every(kpi => kpi.value !== null);
  }, [activePeriod]);

  const activePeriodBudgetPlan = useMemo(() => {
    return activePeriod?.budgetPlan
      ? formatBudgetPlan(activePeriod.budgetPlan.toString()).formatted
      : '';
  }, [activePeriod]);

  const isSubmitAvailable = useMemo(() => {
    const hasKpisWithGoals = activePeriod?.kpis?.some(kpi => kpi.needsMetricGoals) || false;
    const areGoalsValid = !hasKpisWithGoals || selectedGoals.length > 0;

    const hasValidDates = (() => {
      if (!activePeriod?.startDate) return false;
      if (activePeriod.type === 'flight') return Boolean(activePeriod.endDate);
      return true;
    })();

    return (
      Boolean(activePeriod?.budgetPlan) &&
      hasValidDates &&
      activePeriod?.type &&
      activePeriod.repeatEvery &&
      areKpisValid &&
      areGoalsValid
    );
  }, [activePeriod, areKpisValid, selectedGoals]);

  const periodDescriptionMessage = useMemo(() => {
    const isTurned = activePeriod?.isLinkedWithPeriodStart;

    if (!activePeriod?.startDate) return '';

    if (activePeriod.repeatEvery === 'weekly') {
      return isTurned ? (
        t('Следующий круг начнется с понедельника', 'Следующий круг начнется с понедельника')
      ) : (
        <>
          <Trans>Следующий круг начнется с</Trans> {getDayOfWeekInGenitive(activePeriod.startDate)}
        </>
      );
    }

    return isTurned
      ? t('Следующий круг начнется с 1 числа', 'Следующий круг начнется с 1 числа')
      : t('Следующий круг начнется с {{day}} числа', 'Следующий круг начнется с {{day}} числа', {
          day: format(activePeriod.startDate, 'd', {locale: ru}),
        });
  }, [
    activePeriod?.isLinkedWithPeriodStart,
    activePeriod?.repeatEvery,
    activePeriod?.startDate,
    t,
  ]);

  const handleSavePeriods = async () => {
    try {
      const originalMediaplans = data?.currentUser?.company?.unfinishedMediaplans || [];

      function formatPeriodForSave(period: Period, isUpdate: true): AccountsMediaplanUpdateInput;
      function formatPeriodForSave(period: Period, isUpdate?: false): AccountsMediaplanCreateInput;
      function formatPeriodForSave(
        period: Period,
        isUpdate = false,
      ): AccountsMediaplanCreateInput | AccountsMediaplanUpdateInput {
        const baseData = {
          ...(isUpdate && {id: period.id}),
          startOn: period.startDate ? format(period.startDate, 'yyyy-MM-dd') : '',
          type: period.type.toUpperCase() as AccountsMediaplanType,
          budget: period.budgetPlan ?? 0,
          kpis: period.kpis?.map(transformKpis) || [],
        };

        if (period.type === 'flight') {
          return {
            ...baseData,
            endOn: format(period.endDate!, 'yyyy-MM-dd'),
          };
        } else {
          return {
            ...baseData,
            repeat: period.repeatEvery.toUpperCase() as AccountsMediaplanRepeat,
            startOnPeriodBegin: period.isLinkedWithPeriodStart,
          };
        }
      }

      const removedPeriods = originalMediaplans
        .filter(original => !periods.some((period: Period) => period.id === original.id))
        .map(original => ({id: original.id}));

      const newPeriods: AccountsMediaplanCreateInput[] = periods
        .filter((period: Period) => !originalMediaplans.some(original => original.id === period.id))
        .map(period => formatPeriodForSave(period, false));

      const updatedPeriods: AccountsMediaplanUpdateInput[] = periods
        .filter((period: Period) => originalMediaplans.some(original => original.id === period.id))
        .map(period => formatPeriodForSave(period, true));

      await updateMediaplans({
        variables: {
          id: data?.currentUser?.company?.id ?? '',
          new: newPeriods,
          updated: updatedPeriods,
          removed: removedPeriods,
        },
      });

      if (selectedGoals.length > 0 && data?.currentUser?.company?.id) {
        const selectedGoalIds = selectedGoals.map(id => Number(id));

        let freshAttributionModel =
          data.currentUser.company.kpiSettings.attributionModel ||
          YalperAnalyticAttributionModelEnum.AUTOMATIC;
        let freshRevenueGoalId = data.currentUser.company.kpiSettings.revenueGoalId ?? null;

        try {
          const {data: freshData} = await refetch();
          if (freshData.currentUser?.company?.kpiSettings) {
            freshAttributionModel =
              freshData.currentUser.company.kpiSettings.attributionModel ||
              YalperAnalyticAttributionModelEnum.AUTOMATIC;
            freshRevenueGoalId = freshData.currentUser.company.kpiSettings.revenueGoalId ?? null;
          }
        } catch {
          // Refetch failed — use data from current cache as fallback
        }

        try {
          await saveKpiSettings({
            variables: {
              companyId: data.currentUser.company.id,
              settings: {
                attributionModel: freshAttributionModel,
                goals: selectedGoalIds,
                revenueGoalId: freshRevenueGoalId,
              },
            },
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error saving KPI settings:', error);
        }
      }

      if (companyId) {
        let newUrl = `/home/project/sensors?companyId=${companyId}`;

        if (isEdit) {
          newUrl += '&edit=1';
        }

        navigate(newUrl);
      } else {
        navigate(-1);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving periods:', error);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    handleActiveTabClick(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = parseFormattedNumber(e.target.value);

    handleBudgetPlanChange(formattedValue);
  };

  const handleCreateNewProject = () => {
    navigate('/home/project/new');
  };

  const handleKpisChange = (kpis: KpiLocalSettings[]) => {
    updateRequest({kpis});
  };

  const handlePrimaryKpiChange = (primaryKpiId: string) => {
    if (activePeriod && activePeriod.kpis) {
      const updatedKpis = activePeriod.kpis.map(kpi => ({
        ...kpi,
        primary: kpi.id === primaryKpiId,
      }));

      return updateRequest({kpis: updatedKpis, primaryKpiId});
    }

    updateRequest({primaryKpiId});
  };

  const getPrimaryKpiId = (): string | null => {
    return activePeriod?.kpis?.find(kpi => kpi.primary)?.id || null;
  };

  if (error || errorCompanyMediaplans) {
    return <Alert severity='error'>{(error || errorCompanyMediaplans)?.message}</Alert>;
  }
  if (loading || loadingCompanyMediaplans) {
    return <LinearProgress style={{flex: 1}} />;
  }

  if (hasMultipleSystems) {
    return (
      <Box sx={{padding: 2}}>
        <Alert severity='error' sx={{mb: 4}}>
          <Typography>
            <Trans>
              Для корректной работы медиа плана необходимо, чтобы в проекте была подключена только
              одна рекламная система. Отключите одну рекламную систему в этом проекте и создайте
              новый проект для подключения другой системы.
            </Trans>
          </Typography>
        </Alert>
        <PrimaryMediumButton onClick={handleCreateNewProject}>
          <Trans>Создать новый проект</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  }

  return (
    <StyledBox>
      {isEdit ? (
        <SidebarEdit activeStep={'PERIODS'} enableAll />
      ) : (
        <SidebarNew activeStep={'PERIODS'} enableAll />
      )}

      <Box>
        <Typography className={classes.heading}>
          <Trans>Периоды и планы</Trans>
        </Typography>
      </Box>

      <Box className={classes.controls}>
        <Box className={classes.controlsWrapper}>
          {CONTROLS_PERIODS.map(period => (
            <Button
              key={period.key}
              className={clsx(
                classes.controlButton,
                activeControlsPeriod === period.key && classes.controlButtonActive,
              )}
              onClick={() => handlePeriodChange(period.key)}
            >
              <Trans>{capitalize(period.name)}</Trans>
            </Button>
          ))}
        </Box>

        <Box className={classes.controlsWrapper}>
          <Button className={classes.swipeButton} onClick={() => handlePeriodSwipe(-1)}>
            <ArrowBackIosNewIcon />
          </Button>
          <Button className={classes.swipeButton} onClick={() => handlePeriodSwipe(1)}>
            <ArrowBackIosNewIcon />
          </Button>
        </Box>
      </Box>

      <Chart startDate={startDate} endDate={endDate} periods={periods} />

      <Box className={classes.periodsTabsWrapper}>
        {periods && periods.length > 0 && (
          <StyledTabs
            value={activePeriodId}
            onChange={handleTabChange}
            variant='scrollable'
            scrollButtons='auto'
          >
            {periods.map(mapPeriods)}
          </StyledTabs>
        )}
        <Button className={classes.addPeriodButton} onClick={handleAddPeriod} sx={{ml: 2}}>
          <AddIcon className={classes.addPeriodButtonIcon} />
          <Trans>Добавить период</Trans>
        </Button>
      </Box>

      {periods && periods.length > 0 && (
        <Box>
          <Box className={classes.periodForm}>
            <Button
              className={classes.dateButton}
              onClick={() => startDatePickerRef.current?.setOpen(true)}
            >
              <DatePicker
                ref={startDatePickerRef}
                locale={ru}
                selected={activePeriod?.startDate || null}
                onChange={handleStartDateChange}
                dateFormat="'С' d MMMM yyyy"
                minDate={minStartDate}
                filterDate={(date: Date) => canSelectStartDate(date, activePeriodId)}
                renderDayContents={renderDayWithHint}
                placeholderText={t('Выберите дату', 'Выберите дату')}
                onClickOutside={() => startDatePickerRef.current?.setOpen(false)}
              />
              <InputAdornment position='end'>
                <TodayIcon className={classes.periodInputIcon} />
              </InputAdornment>
            </Button>

            <Box className={classes.periodButtonsBlock}>
              <Button
                className={clsx(
                  classes.periodButton,
                  activePeriod && activePeriod.type === 'repeatable' && classes.periodButtonActive,
                )}
                onClick={() => handleChangePeriodType('repeatable')}
              >
                <Trans>{t('Бессрочный', 'Бессрочный')}</Trans>
                <AllInclusiveIcon
                  className={clsx(classes.periodButtonIcon, classes.periodButtonIconBlue)}
                />
              </Button>
              <Button
                className={clsx(
                  classes.periodButton,
                  activePeriod && activePeriod.type === 'flight' && classes.periodButtonActive,
                )}
                onClick={() => handleChangePeriodType('flight')}
              >
                <Trans>{t('Срочный (флайт)', 'Срочный (флайт)')}</Trans>
                <MoveDownIcon
                  className={clsx(classes.periodButtonIcon, classes.periodButtonIconGreen)}
                />
              </Button>
            </Box>

            {activePeriod && (
              <Button
                className={classes.dateButton}
                onClick={() => endDatePickerRef.current?.setOpen(true)}
              >
                <DatePicker
                  ref={endDatePickerRef}
                  locale={ru}
                  openToDate={new Date()}
                  selected={activePeriod.endDate || null}
                  minDate={activePeriod.startDate || minStartDate}
                  onChange={handleEndDateChange}
                  dateFormat="'По' d MMMM yyyy"
                  placeholderText={
                    activePeriod.type === 'flight'
                      ? t('Выберите дату', 'Выберите дату')
                      : t('Дата окончания', 'Дата окончания')
                  }
                  filterDate={(date: Date) => canSelectEndDate(date, activePeriod)}
                  renderDayContents={renderDayWithHint}
                  onClickOutside={() => endDatePickerRef.current?.setOpen(false)}
                />
                <InputAdornment position='end'>
                  <TodayIcon className={classes.periodInputIcon} />
                </InputAdornment>
              </Button>
            )}

            {activePeriod?.type === 'repeatable' && (
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <Box className={classes.periodSwitchWrapper}>
                  <Typography className={classes.periodSwitchDescription}>
                    <Trans>Привязать к началу месяца</Trans>
                  </Typography>
                  <Box sx={{ml: 4}}>
                    <Switch
                      checked={activePeriod.isLinkedWithPeriodStart}
                      onChange={handleLinkedWithPeriodStartChange}
                      inputProps={{'aria-label': 'Привязать к началу месяца'}}
                    />
                  </Box>
                </Box>
                <Box sx={{mt: 2.5}}>
                  <Typography className={classes.periodSwitchDescription}>
                    <Trans>{periodDescriptionMessage}</Trans>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{mt: 16}}>
            <Typography className={classes.periodFormHeading}>
              <Trans>План бюджета на период</Trans>
            </Typography>

            <Box sx={{mt: 4}}>
              <Typography className={classes.periodFormDescription}>
                <Trans>Укажите плановое значение, с НДС</Trans>
              </Typography>
            </Box>

            <Box sx={{mt: 8}} className={classes.budgetPlanWrapper}>
              <LegacyTextField
                className={classes.periodFormInput}
                type='text'
                value={activePeriodBudgetPlan}
                placeholder={t('План на период', 'План на период')}
                onChange={handleInputChange}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <CurrencyRubleIcon className={classes.periodInputIcon} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {!!activePeriod?.budgetPlan && !!activePeriod.startDate && (
                <Box className={classes.budgetPerDayWrapper}>
                  <Box className={classes.budgetPerDayTextWrapper}>
                    {activePeriod.type === 'repeatable' && (
                      <Typography className={classes.budgetPerDayText}>
                        <Trans>Примерно</Trans>
                      </Typography>
                    )}
                    <Typography className={classes.budgetPerDayText}>
                      {calculateDailyBudget(activePeriod)?.toLocaleString('ru-RU')} ₽{' '}
                      <Trans>в день</Trans>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{mt: 16}}>
        {activePeriod && (
          <KpiPlanForm
            selectedKpis={activePeriod.kpis || []}
            primaryKpiId={getPrimaryKpiId()}
            mediaplanType={
              data?.currentUser?.company?.mediaplanType === 'MEDIA' ? 'impressions' : 'clicks'
            }
            analyticGoals={dataGoals?.currentUser?.company?.analyticGoals || []}
            isLoading={loadingGoals}
            error={errorGoals?.message}
            kpiSettings={data?.currentUser?.company?.kpiSettings}
            selectedGoals={selectedGoals}
            onSelectedGoalsChange={setSelectedGoals}
            onKpisChange={handleKpisChange}
            tokens={data?.currentUser?.company?.tokens || []}
            onPrimaryKpiChange={handlePrimaryKpiChange}
          />
        )}
      </Box>
      <Box
        sx={{
          mt: 24,
          mb: 24,
        }}
      >
        <PrimaryMediumButton disabled={!isSubmitAvailable} onClick={handleSavePeriods}>
          <Trans>Сохранить и перейти к сенсорам</Trans>
        </PrimaryMediumButton>
        {isEdit && <BackToProjectButton />}
      </Box>
    </StyledBox>
  );
};

export default Periods;
